/**
 * Shared pagination bar for analytics tables.
 *
 * Kept presentation-only — the parent owns the page index and passes a
 * setter. Works for both backend (totalCount known up-front) and
 * client-side (totalCount = rows.length) pagination.
 */
import type { CSSProperties } from 'react';

export interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (next: number) => void;
  /** Hide the bar when only one page of rows exists. */
  hideWhenSinglePage?: boolean;
}

export const Pagination = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  hideWhenSinglePage = true,
}: PaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const clamped = Math.min(Math.max(0, page), totalPages - 1);
  if (hideWhenSinglePage && totalPages <= 1) return null;

  const rangeFrom = totalCount === 0 ? 0 : clamped * pageSize + 1;
  const rangeTo = Math.min(totalCount, (clamped + 1) * pageSize);
  const canPrev = clamped > 0;
  const canNext = clamped < totalPages - 1;

  return (
    <div style={barStyle}>
      <span style={rangeStyle}>
        {rangeFrom}–{rangeTo} / {totalCount}
      </span>
      <div style={spacer} />
      <button
        type="button"
        style={buttonStyle(!canPrev)}
        disabled={!canPrev}
        onClick={() => onPageChange(clamped - 1)}
        aria-label="Previous page"
      >
        ‹
      </button>
      <span style={pageStyle}>
        {clamped + 1} / {totalPages}
      </span>
      <button
        type="button"
        style={buttonStyle(!canNext)}
        disabled={!canNext}
        onClick={() => onPageChange(clamped + 1)}
        aria-label="Next page"
      >
        ›
      </button>
    </div>
  );
};

const barStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8,
  padding: '4px 0',
  fontSize: 12,
  color: 'var(--streamlain-v2-text-secondary)',
};

const rangeStyle: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};

const spacer: CSSProperties = { flex: 1 };

const pageStyle: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  minWidth: 48,
  textAlign: 'center',
};

function buttonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '2px 10px',
    borderRadius: 6,
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    background: 'var(--streamlain-v2-layer-background-primary)',
    color: disabled
      ? 'var(--streamlain-v2-text-disabled)'
      : 'var(--streamlain-v2-text-primary)',
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    lineHeight: 1,
  };
}
